import React, { FC, useCallback, useEffect, useMemo } from "react";
import PanelBackButton from "containers/RightPanel/components/PanelBackButton";
import {
  ContentWrapper,
  Heading,
} from "containers/RightPanel/RightPanel.styles";
import { ButtonColor, ButtonType, ButtonVariant } from "enums/Button";
import { useFormik } from "formik";
import { Map } from "immutable";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { ChangingAddressFormProps } from "types/OutgoingForms/changingAddressPanel";
import AddressSearch from "../../../../components/Addresses/components/AddressSearch";
import Button from "../../../../components/Button/Button";
import { setAddress as setCartAddress } from "../../../../reduxFolder/reducers/cart";
import { updateAddress } from "../../../../reduxFolder/reducers/outgoing";
import { openRightPanel } from "../../../../reduxFolder/reducers/rightPanel";
import { destinationsSelectAddresses } from "../../../../reduxFolder/selectors/destinationSelectors";
import { outgoingSelectDetailedItems } from "../../../../reduxFolder/selectors/outgoingSelectors";
import {
  AddButton,
  AddressesListWrapper,
  ButtonWrapper,
  ContentContainer,
  Form,
} from "./ChangingAddressPanel.styles";
import { changingAddressPanelFormik } from "./handleChangingAddressPanelFormik";

const ChangingAddressPanel: FC<ChangingAddressFormProps> = React.memo(
  ({
    panelData,
    destinations,
    updateAddress,
    setCartAddress,
    openRightPanel,
    detailedItem,
  }) => {
    const { t } = useTranslation("common");
    // The address panel hands a just-added address over as `addedAddress`. It
    // is consumed here (preselected, submitted on mount) and stripped from the
    // data this picker passes on, or the next "Change address" would resubmit
    // it.
    const addedAddress = panelData.get("addedAddress");
    const ownPanelData = useMemo(
      () => panelData.delete("addedAddress"),
      [panelData],
    );
    const {
      handleSubmit,
      submitForm,
      values: { addressId },
      isSubmitting,
      setFieldValue,
    } = useFormik(
      changingAddressPanelFormik(
        ownPanelData,
        destinations,
        updateAddress,
        setCartAddress,
        openRightPanel,
        addedAddress,
      ),
    );
    const isCartVariant = panelData.get("isCartVariant");
    const returnPreviousPanel = panelData.get("returnPreviousPanel");
    const id = panelData.get("detailedItemID");
    const detailed = isCartVariant
      ? panelData.get("cart")
      : detailedItem.get(id, Map());

    const panelProps = {
      initialPanelData: ownPanelData,
      returnPreviousPanel: "CHANGING_ADDRESS",
    };
    const handleBack = useCallback(
      () => openRightPanel(returnPreviousPanel, ownPanelData),
      [openRightPanel, returnPreviousPanel, ownPanelData],
    );
    const handleAddressSelect = useCallback(
      (id: string | number) =>
        setFieldValue("addressId", addressId === id ? null : id),
      [addressId, setFieldValue],
    );

    // A just-added address is saved right away through the same submit as
    // "Save changes", spinner and error handling included.
    useEffect(() => {
      if (addedAddress) submitForm();
    }, [addedAddress, submitForm]);

    return (
      <ContentWrapper>
        <Heading $isBordered>
          <PanelBackButton onClick={handleBack}>
            {detailed.get("sku")}:
          </PanelBackButton>
          {t("parcels.changingAddress")}
        </Heading>
        <ContentContainer>
          <AddressSearch />
          <Form onSubmit={handleSubmit}>
            <AddressesListWrapper
              editable
              onSelect={handleAddressSelect}
              onSubmit={handleSubmit}
              selectedAddress={addressId}
              {...panelProps}
            />
            <AddButton {...panelProps} />
            <ButtonWrapper>
              <Button
                type={ButtonType.Submit}
                color={ButtonColor.Primary}
                variant={ButtonVariant.Filled}
                isLoading={isSubmitting}
                disabled={!addressId || isSubmitting}
              >
                {t("common.saveChanges")}
              </Button>
            </ButtonWrapper>
          </Form>
        </ContentContainer>
      </ContentWrapper>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    destinations: destinationsSelectAddresses,
    detailedItem: outgoingSelectDetailedItems,
  }),
  {
    updateAddress,
    setCartAddress,
    openRightPanel,
  },
);

export default withConnect(ChangingAddressPanel);
